import React, { useEffect, useRef, useState } from 'react';

import { LENS_LAYOUT, type LensCardState, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CodeIcon from '@mui/icons-material/Code';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import ErrorBoundary from '~/components/Common/ErrorBoundary';
import { FONT_STACK_SUISSE_INTL } from '~/components/brand';

import type { LensCardAlignment, LensCardTitleSize, LensContract } from '~/types/leLens';

import LensCardFilterIndicator, { type LensCardNarrowingFilter } from './LensCardFilterIndicator';
import { LENS_DRAG_HANDLE_CLASS } from './cardGrid';
import { type ContractChipInfo, contractChipInfo } from './cardState';

export type LensCardShellFooter = {
  /** Where the card's rows came from: a catalogue source name or custom analysis.
   *  Used for restricted-state copy and source inspection, not repeated on the card face. */
  source: string;
  /** Set alongside `source` when it names a governed dataset, so its menu item
   *  can open that dataset's Data catalogue detail screen. */
  catalogueId?: string;
  freshness?: string;
  /** Row count when the state is 'ready', or the truncated banner sentence
   *  when the state is 'truncated'. The shell only styles it differently. */
  rowCountText?: string;
  /** Rendered inline straight after the dataset name, dot-separated from it.
   *  The caller composes the text (e.g. the underlying source table and the
   *  period covered); the shell only lays it out. Kept to one short phrase:
   *  this sits on the same quiet caption line as everything else in the row,
   *  never a second line under it. */
  detail?: React.ReactNode;
  restrictedNote?: string;
  narrowedBy?: Array<LensCardNarrowingFilter>;
  /** A cross-filter is active elsewhere on the board, but none of its fields
   * exist in this card's returned rows. The header icon explains why this
   * card stays unchanged without taking a footer row from its chart. */
  crossFilterUnaffectedNote?: string;
};

export type LensCardShellEmpty = {
  headline: string;
  subtitle?: string;
  actionLabel?: string;
  actionDisabled?: boolean;
  onAction?: () => void;
};

export type LensCardShellError = {
  message: string;
  /** Plain-language next step. Technical field names belong in `detail`. */
  guidance?: string;
  /** Raw driver text. Never rendered directly: it sits behind the disclosure
   *  so a viewer does not get table and column names on their screen. */
  detail?: string;
  onRetry?: () => void;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
};

export type LensCardLoadingKind = 'chart' | 'table' | 'metric' | 'insight';
export type LensCardEditTab = 0 | 1 | 2 | 3;

export type LensCardShellProps = {
  title: string;
  description?: string;
  showDescription?: boolean;
  icon?: React.ReactNode;
  contract?: LensContract;
  /** The earlier card that resolves to this exact visual answer. */
  duplicateOf?: string;
  state: LensCardState;
  /** Centres the title and body, for a card that is mostly one number. */
  compact?: boolean;
  /** Uses the six-row metric composition after an editor deliberately shrinks a KPI. */
  tight?: boolean;
  /** Optional override for the card's human-readable header placement. */
  titleAlignment?: LensCardAlignment;
  /** Optional override for the card title's scale. */
  titleSize?: LensCardTitleSize;
  /** Keep the metric header compact above its value. */
  kpiLabel?: boolean;
  /** A direct-manipulation canvas frame owns the card's full height. */
  fillFrame?: boolean;
  /**
   * The plot's real height, once the browser has laid the card out. Lets a
   * caller that had to estimate its own chrome replace the estimate with the
   * measurement.
   */
  onBodyMeasured?: (height: number) => void;
  cardMinHeight: number;
  bodyHeight: number;
  /**
   * Vertical alignment for the body's own content. Defaults to 'center', the
   * long-standing behaviour for a chart or table (which fills the box either
   * way) and for the loading/empty/error placeholders (small, message-shaped
   * content that reads better centred in the space it has). A caller whose
   * content is a fixed-height figure sitting directly under the title, on a
   * card whose real total height can leave this box with less room than that
   * content needs, should pass 'top': centring on a squeezed box (however
   * little of it flex-shrink leaves) puts half the figure over the header
   * above it, where flowing from the top instead only ever crowds or clips
   * whatever comes after, never the title before it.
   */
  bodyAlign?: 'center' | 'top';
  /** Horizontal placement for a compact headline and its supporting text. */
  bodyContentAlignment?: LensCardAlignment;
  footer: LensCardShellFooter;
  /** The shared dataset actually bound to this card. Kept out of the card face
   *  and disclosed in the actions menu beside View data and View SQL. */
  datasetName?: string;
  /**
   * The card's own reading: one line about what its numbers say, rendered as a
   * strip between the body and the footer. Never a card of its own, so a board
   * gains commentary without losing a chart to it. Omitted renders nothing and
   * leaves the body sitting straight on the footer, as it always did.
   */
  reading?: React.ReactNode;
  /** Charts use a quiet disclosure without a separate footer rule. */
  readingDivider?: boolean;
  /** Canvas-level provenance avoids repeating a dense footer in every card. */
  showFooter?: boolean;
  /** Dashboard reading is intentionally quiet; a caller may opt into the
   * narrow download-only tray below without leaking authoring controls. */
  showActions?: boolean;
  /** Readers can inspect SQL and export results; authoring controls require edit mode. */
  actionScope?: 'authoring' | 'export-only';
  empty?: LensCardShellEmpty;
  /** Same shape as `empty`: a caller that knows which owner or permission is
   *  missing can say so here. Omitted means the shell falls back to a plain
   *  copy-to-clipboard action, since there is no "request access" endpoint. */
  restricted?: LensCardShellEmpty;
  error?: LensCardShellError;
  loadingKind: LensCardLoadingKind;
  /** A card whose work is owned by askRichard can replace the generic skeleton
   * with the named R mark and a human-readable status. */
  loadingContent?: React.ReactNode;
  onRefresh: () => void;
  /** Omitted when there is nothing to export, which hides the item entirely. */
  onExportCsv?: () => void;
  onExportImage?: () => void;
  /** Omitted when the card has no SQL to show, such as an insight card. */
  onViewSql?: () => void;
  onViewData?: () => void;
  onCollapseRows?: () => void;
  refreshing: boolean;
  refreshLabel?: string;
  canEdit: boolean;
  onEdit: (tab?: LensCardEditTab) => void;
  /** Opens the editor directly on the row-order controls. */
  onSort?: () => void;
  onDelete: () => void;
  /** Omitted while the clone-and-save flow lives elsewhere; hides the item
   *  entirely rather than offering an action that does nothing. */
  onDuplicate?: () => void;
  /** Explicit cross-section destinations. The grid drag only rearranges the
   * current section, so moving between sections must name the destination. */
  onMoveToSection?: Array<{ label: string; onMove: () => void }>;
  /** Present in the unified shell only; starts a scoped, editable askRichard draft. */
  onAskRichard?: () => void;
  /** Opens a catalogue-backed dataset from its card-menu disclosure. */
  onOpenCatalogueDataset?: (catalogueId: string) => void;
  /** Present for editors on a draggable canvas. The explicit handle prevents
   * chart interaction and card actions being mistaken for a drag gesture. */
  dragHandle?: { isDragging: boolean };
  /** Shown only in the dashboard's explicit multi-select mode. */
  selectionControl?: {
    checked: boolean;
    label: string;
    onChange: () => void;
  };
  children?: React.ReactNode;
};

/**
 * The one shape every Lens card renders through. A card is a governed
 * answer, not a picture, so the header, body and footer are fixed and the
 * five states below are visually distinct by construction: nothing here
 * falls through to an unstyled default, which is what let a zero-row chart
 * print a diagnostic string and an errored card look identical to an empty
 * one.
 */
export default function LensCardShell({
  title,
  description,
  showDescription = true,
  icon,
  contract,
  duplicateOf,
  state,
  compact = false,
  tight = false,
  titleAlignment,
  titleSize,
  kpiLabel = false,
  fillFrame = false,
  onBodyMeasured,
  cardMinHeight,
  bodyHeight,
  bodyAlign = 'center',
  bodyContentAlignment = 'center',
  footer,
  datasetName,
  reading,
  readingDivider = true,
  showFooter = true,
  showActions = true,
  actionScope = 'authoring',
  empty,
  restricted,
  error,
  loadingKind,
  loadingContent,
  onRefresh,
  onExportCsv,
  onExportImage,
  onViewSql,
  onViewData,
  onCollapseRows,
  refreshing,
  refreshLabel = 'Refresh data',
  canEdit,
  onEdit,
  onSort,
  onDelete,
  onDuplicate,
  onMoveToSection,
  onAskRichard,
  onOpenCatalogueDataset,
  dragHandle,
  selectionControl,
  children,
}: LensCardShellProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [gripHovered, setGripHovered] = useState(false);
  const hasFilterIndicator = Boolean(footer.narrowedBy?.length || footer.crossFilterUnaffectedNote);

  // How much room the plot really has, reported to the caller that sized it.
  //
  // The caller works this out by subtracting a constant from the frame, and the
  // constant cannot know how many lines the description wrapped to. This box is
  // a flex item with flex-basis 0%, so its used height is the space genuinely
  // left whatever height it was handed: measuring it is measuring the answer.
  //
  // Deliberately not the same as letting the plot fill its container. That was
  // tried and reverted: the chart has a resize observer of its own that grows a
  // container with no definite height, and the two observers chase each other
  // to canvases twenty thousand pixels tall. The box keeps its height, the
  // chart keeps a pixel number, and only the number changes.
  useEffect(() => {
    const node = bodyRef.current;
    if (!node || !onBodyMeasured || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const report = () => onBodyMeasured(Math.round(node.getBoundingClientRect().height));
    report();
    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => observer.disconnect();
  }, [onBodyMeasured]);

  const chip = contractChipInfo(tokens, contract);
  const menuOpen = Boolean(menuAnchor);
  const exportOnly = actionScope === 'export-only';
  const textAlignment = titleAlignment ?? (compact ? 'center' : 'left');
  // A centred title is centred on the card, not on the space left after the
  // action tray. Overlay the tray and reserve the same clearance on both
  // sides so a visible menu button cannot pull the heading off-axis.
  const overlayActions = actionScope === 'authoring' || textAlignment === 'center';
  const trayWidth = 34 * (1 + Number(Boolean(onCollapseRows)) + Number(Boolean(dragHandle)));
  // Use the same type scale for every card; wrapping handles narrower cards.
  const titleFontSize = titleSize === 'small' ? '1rem' : titleSize === 'large' ? '1.375rem' : '1.125rem';
  // Reserve two lines on chart headers so different title lengths align.
  const TITLE_LINE_HEIGHT = 1.3;
  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(fillFrame
          ? {
              // The canvas frame is the card's hard boundary. A table whose
              // natural content is taller must scroll inside that boundary;
              // it must never grow past the grid item and cover the card
              // below while the resize handle stays behind.
              height: '100%',
              maxHeight: '100%',
              minHeight: 0,
              overflow: 'hidden',
            }
          : { minHeight: cardMinHeight }),
        minWidth: 0,
        ...(state === 'error'
          ? { borderLeftColor: theme.palette.error.main, borderLeftWidth: 4, borderLeftStyle: 'solid' }
          : {}),
        ...(dragHandle?.isDragging ? { boxShadow: theme.shadows[8], cursor: 'grabbing', zIndex: 2 } : {}),
        // Metric trends sit alongside their headline. Let that graphic respond
        // to the card's own width rather than the viewport: a narrow tile can
        // hide the secondary visual and leave the headline legible, while the
        // same card shows it again whenever it has room.
        containerType: 'inline-size',
        position: 'relative',
        '&:hover [data-testid="card-action-tray"], &:focus-within [data-testid="card-action-tray"]': {
          opacity: 1,
          pointerEvents: 'auto',
        },
        '@container (max-width: 340px)': {
          '& .MuiCardContent-root': {
            p: kpiLabel && tight ? 0.5 : 1.25,
            '&:last-child': { pb: kpiLabel && tight ? 0.5 : 1.25 },
          },
        },
        '@container (min-width: 880px)': {
          '& .MuiCardContent-root': { p: 2, '&:last-child': { pb: 2 } },
        },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          position: 'relative',
          // The footer has its own top inset after its divider. Keeping the
          // full body padding below it made the provenance row sit visibly
          // high in its lane; retain a balanced card edge instead.
          ...(showFooter ? { '&:last-child': { pb: 2 }, pb: 2 } : {}),
          ...(kpiLabel && compact ? { p: tight ? 1 : 1.5, '&:last-child': { pb: tight ? 1 : 1.5 } } : {}),
        }}
      >
        {selectionControl && (
          <Checkbox
            checked={selectionControl.checked}
            inputProps={{ 'aria-label': selectionControl.label }}
            onChange={selectionControl.onChange}
            size="small"
            sx={{ position: 'absolute', right: 2, top: 2, zIndex: 4 }}
          />
        )}
        <Stack
          direction="row"
          alignItems={compact ? 'center' : 'flex-start'}
          justifyContent="space-between"
          spacing={textAlignment === 'center' ? 0 : 1}
          sx={{
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <Box
            className="lens-card-heading"
            sx={{
              flex: 1,
              minWidth: 0,
              position: 'relative',
              ...(textAlignment === 'center' && (showActions || hasFilterIndicator)
                ? { px: dragHandle ? 7 : 3.5 }
                : {
                    ...(hasFilterIndicator ? { pl: 3.5 } : {}),
                    ...(overlayActions && showActions ? { pr: `${trayWidth}px` } : {}),
                  }),
            }}
          >
            <LensCardFilterIndicator narrowedBy={footer.narrowedBy} unaffectedNote={footer.crossFilterUnaffectedNote} />
            <Typography
              component="h2"
              // The native title attribute carries the full text once the
              // clamp below cuts it, so a clipped heading is never a dead end.
              title={title}
              variant="h6"
              sx={{
                color: 'text.primary',
                display: '-webkit-box',
                fontFamily: FONT_STACK_SUISSE_INTL,
                fontSize: kpiLabel ? 12 : titleFontSize,
                fontWeight: 600,
                letterSpacing: kpiLabel && !tight ? '0.06em' : 'normal',
                lineHeight: TITLE_LINE_HEIGHT,
                ...(kpiLabel ? { color: 'text.secondary' } : {}),
                ...(kpiLabel ? { mb: tight ? 0.25 : 0.5 } : {}),
                minHeight: `${TITLE_LINE_HEIGHT * 2}em`,
                overflow: 'hidden',
                overflowWrap: 'break-word',
                textAlign: textAlignment,
                textTransform: kpiLabel ? 'uppercase' : 'none',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: kpiLabel ? 3 : 2,
              }}
            >
              {title}
            </Typography>
            {showDescription && description && (
              <Typography
                color="text.secondary"
                variant="body2"
                title={description}
                sx={{
                  display: '-webkit-box',
                  mt: 0.5,
                  overflow: 'hidden',
                  overflowWrap: 'anywhere',
                  textAlign: textAlignment,
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: compact ? 1 : 2,
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
          {showActions && (
            <>
              <Stack
                alignItems="center"
                direction="row"
                spacing={0.5}
                sx={{
                  ...(selectionControl ? { display: 'none' } : {}),
                  '@media (hover: none), (pointer: coarse)': {
                    opacity: 1,
                    pointerEvents: 'auto',
                    position: 'relative',
                  },
                  backgroundColor:
                    actionScope === 'authoring' ? alpha(theme.palette.background.paper, 0.94) : 'transparent',
                  borderRadius: 0.75,
                  flexShrink: 0,
                  opacity: menuOpen || gripHovered || dragHandle?.isDragging ? 1 : 0,
                  pointerEvents: menuOpen || gripHovered || dragHandle?.isDragging ? 'auto' : 'none',
                  position: overlayActions ? 'absolute' : 'relative',
                  // Tight metrics have less padding than charts; keep the
                  // controls' focus outline inside the card at that density.
                  right: overlayActions ? (kpiLabel && tight ? 0 : -4) : undefined,
                  top: overlayActions ? (kpiLabel && tight ? 0 : -4) : undefined,
                  alignSelf: 'flex-start',
                  zIndex: 3,
                  transition: reduceMotion ? 'none' : 'opacity 120ms ease-out',
                }}
                data-testid="card-action-tray"
              >
                {onCollapseRows && (
                  <IconButton aria-label="Collapse table" color="primary" onClick={onCollapseRows} size="small">
                    <KeyboardArrowUpIcon fontSize="small" />
                  </IconButton>
                )}
                {dragHandle && (
                  <Tooltip
                    // Switching between uncontrolled and controlled left it on
                    // screen through the drag, where it sits over the very area the
                    // card is being dropped into and hides the placeholder the drag
                    // exists to follow. Controlled throughout instead.
                    disableHoverListener
                    onClose={() => setGripHovered(false)}
                    onOpen={() => setGripHovered(true)}
                    open={gripHovered && !dragHandle.isDragging}
                    title="Drag or use arrow keys to move"
                  >
                    <IconButton
                      aria-label={`Move ${title}`}
                      className={LENS_DRAG_HANDLE_CLASS}
                      onPointerEnter={() => setGripHovered(true)}
                      onPointerLeave={() => setGripHovered(false)}
                      size="small"
                      sx={{
                        color: theme.palette.text.secondary,
                        cursor: 'grab',
                        height: compact ? 28 : undefined,
                        touchAction: 'none',
                        width: compact ? 28 : undefined,
                        '&:active': { cursor: 'grabbing' },
                      }}
                    >
                      <OpenWithIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={exportOnly ? 'Inspect and download' : 'Card actions'}>
                  <IconButton
                    aria-label={exportOnly ? `Inspect ${title}` : `Actions for ${title}`}
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                    size="small"
                    sx={{
                      ...(compact ? { height: 28, width: 28 } : {}),
                      // Dashboards are meant to be revisited and adjusted. Keep
                      // this quiet rather than invisible on desktop so editing a
                      // chart does not depend on discovering a hover-only tray.
                      opacity: menuOpen ? 1 : { xs: 1, md: 0.58 },
                      transition: reduceMotion ? 'none' : 'opacity 120ms',
                      '&:focus-visible': { opacity: 1 },
                      '.MuiCard-root:hover &': { opacity: 1 },
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Menu
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                open={menuOpen}
                slotProps={{ paper: { sx: { minWidth: 244, mt: 0.5 } } }}
              >
                {/* Sorting stays available from the reading surface; opening it
                moves the owner into the focused Format control. */}
                {!exportOnly && canEdit && (
                  <MenuItem
                    key="edit-card"
                    onClick={() => {
                      setMenuAnchor(null);
                      onEdit(0);
                    }}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Edit card" secondary="Data, filters and visual settings" />
                  </MenuItem>
                )}
                {onSort && (
                  <MenuItem
                    key="sort-chart"
                    onClick={() => {
                      setMenuAnchor(null);
                      onSort();
                    }}
                  >
                    <ListItemIcon>
                      <SwapVertOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Sort chart" secondary="Date, label or value order" />
                  </MenuItem>
                )}
                {((!exportOnly && canEdit) || onSort) && <Divider key="edit-divider" />}
                {!exportOnly && [
                  <ListSubheader disableSticky key="explore-heading" sx={{ lineHeight: 2.5 }}>
                    Explore
                  </ListSubheader>,
                  ...(onAskRichard
                    ? [
                        <MenuItem
                          key="ask-richard"
                          onClick={() => {
                            setMenuAnchor(null);
                            onAskRichard();
                          }}
                        >
                          <ListItemIcon>
                            <ForumOutlinedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Ask askRichard about this</ListItemText>
                        </MenuItem>,
                      ]
                    : []),
                  <MenuItem
                    key="refresh"
                    disabled={refreshing}
                    onClick={() => {
                      setMenuAnchor(null);
                      onRefresh();
                    }}
                  >
                    <ListItemIcon>
                      <RefreshIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{refreshing ? 'Refreshing…' : refreshLabel}</ListItemText>
                  </MenuItem>,
                  ...(onExportCsv || onExportImage ? [<Divider key="export-divider" />] : []),
                ]}
                {datasetName && (
                  <MenuItem
                    aria-label={`Dataset: ${datasetName}`}
                    onClick={() => {
                      setMenuAnchor(null);
                      if (footer.catalogueId && onOpenCatalogueDataset) {
                        onOpenCatalogueDataset(footer.catalogueId);
                      }
                    }}
                  >
                    <ListItemIcon>
                      <StorageOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Dataset"
                      secondary={datasetName}
                      secondaryTypographyProps={{ noWrap: true, title: datasetName }}
                    />
                  </MenuItem>
                )}
                {onViewData && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onViewData();
                    }}
                  >
                    <ListItemText>View data</ListItemText>
                  </MenuItem>
                )}
                {onViewSql && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onViewSql();
                    }}
                  >
                    <ListItemIcon>
                      <CodeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View SQL</ListItemText>
                  </MenuItem>
                )}
                {!exportOnly && (onExportCsv || onExportImage) && (
                  <ListSubheader disableSticky sx={{ lineHeight: 2.5 }}>
                    Export
                  </ListSubheader>
                )}
                {onExportImage && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onExportImage();
                    }}
                  >
                    <ListItemIcon>
                      <ImageOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Download image</ListItemText>
                  </MenuItem>
                )}
                {onExportCsv && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onExportCsv();
                    }}
                  >
                    <ListItemIcon>
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Download loaded rows as CSV"
                      secondary="Exports the rows shown by this card"
                    />
                  </MenuItem>
                )}
                {!exportOnly && canEdit ? <Divider /> : null}
                {!exportOnly && canEdit
                  ? [
                      ...(onMoveToSection?.length
                        ? [
                            <ListSubheader key="move-section" sx={{ lineHeight: 2.2 }}>
                              Move to section
                            </ListSubheader>,
                            ...onMoveToSection.map(({ label, onMove }) => (
                              <MenuItem
                                key={`move:${label}`}
                                onClick={() => {
                                  setMenuAnchor(null);
                                  onMove();
                                }}
                              >
                                <ListItemIcon>
                                  <DriveFileMoveOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{label}</ListItemText>
                              </MenuItem>
                            )),
                            <Divider key="move-divider" />,
                          ]
                        : []),
                      onDuplicate && (
                        <MenuItem
                          key="duplicate"
                          onClick={() => {
                            setMenuAnchor(null);
                            onDuplicate();
                          }}
                        >
                          <ListItemIcon>
                            <ContentCopyOutlinedIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Duplicate card</ListItemText>
                        </MenuItem>
                      ),
                      <MenuItem
                        key="delete"
                        onClick={() => {
                          setMenuAnchor(null);
                          onDelete();
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <ListItemIcon sx={{ color: 'inherit' }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Delete card</ListItemText>
                      </MenuItem>,
                    ]
                  : null}
              </Menu>
            </>
          )}
        </Stack>

        <Box
          data-lens-card-body
          ref={bodyRef}
          sx={{
            // Confirmed live: even with the flex-basis fix below, flex-shrink
            // still collapses this box to height 0 when the card's real total
            // height leaves no room to spare once the header and footer take
            // theirs (min-height: 0 lets it, and something has to give if the
            // content above and below is fixed). Centring on that collapsed
            // point put half the content over the header regardless. Aligning
            // from the top instead means a squeeze crowds or clips whatever
            // comes after the content, never the title before it.
            // `bodyAlign` is deliberately a vertical decision only. Metric
            // cards flow their figure from the top so it cannot collide with a
            // long header, but that must not turn a headline KPI into a
            // left-aligned number. Keep the body's horizontal centre line for
            // every state; child charts and tables still take the full width.
            alignItems:
              bodyAlign === 'top'
                ? 'flex-start'
                : bodyContentAlignment === 'left'
                  ? 'flex-start'
                  : bodyContentAlignment === 'right'
                    ? 'flex-end'
                    : 'center',
            display: 'flex',
            // 'flex: 1' is shorthand for 'flex: 1 1 0%': a 0% flex-basis wins
            // over the `height` below for main-axis sizing, so this box's
            // resolved height ignored `bodyHeight` entirely before this. Kept
            // even though it alone does not stop the shrink-to-zero case
            // above: it is still what makes `height` count as this box's
            // actual starting size rather than zero, on every card whose
            // total height does have room to spare.
            flex: '1 1 auto',
            height: bodyHeight,
            // Unusually long comparison text remains reachable instead of clipped.
            overflow: kpiLabel || (state !== 'ready' && state !== 'loading') ? 'auto' : 'hidden',
            justifyContent:
              state === 'error' || state === 'empty' ? 'flex-start' : bodyAlign === 'top' ? 'flex-start' : 'center',
            minHeight: 0,
            minWidth: 0,
            mt: kpiLabel && compact ? 0 : 1,
            width: '100%',
          }}
        >
          {state === 'loading' &&
            (loadingContent ?? <LensCardLoadingBody kind={loadingKind} reduceMotion={reduceMotion} />)}
          {/* The payload is a separate prop from the state, so a caller can set
              one without the other. Falling back rather than rendering nothing
              means the worst case is a card that says little, not a card that
              silently says nothing at all. */}
          {state === 'empty' && <LensCardEmptyBody empty={empty ?? { headline: 'Nothing to show for this card.' }} />}
          {state === 'error' && (
            <LensCardErrorBody
              detailsOpen={detailsOpen}
              error={error ?? { message: 'This card could not load.' }}
              onToggleDetails={() => setDetailsOpen((current) => !current)}
              reduceMotion={reduceMotion}
            />
          )}
          {state === 'restricted' && <LensCardRestrictedBody footer={footer} restricted={restricted} title={title} />}
          {(state === 'ready' || state === 'truncated') && (
            <ErrorBoundary fallbackTitle="This card">{children}</ErrorBoundary>
          )}
        </Box>

        {reading && (state === 'ready' || state === 'truncated') ? (
          <Box
            sx={{
              // Quiet by construction: a reading supports the answer above it
              // and must never compete with it for the eye. A hairline is all
              // the separation it needs, and unlike a filled strip it stays
              // correct at every card padding the container queries above set.
              borderTop: readingDivider ? `1px solid ${alpha(theme.palette.text.primary, 0.09)}` : undefined,
              flexShrink: 0,
              mt: readingDivider ? 1 : 0.5,
              pt: readingDivider ? 1 : 0,
              // A closed chart disclosure sits on the card edge without
              // taking plot space. Exceptional status footers keep their own lane.
              '&:has(> details[data-lens-card-description])': {
                mt: 0,
                minHeight: showFooter ? 24 : 0,
                position: showFooter ? 'relative' : undefined,
              },
            }}
          >
            {reading}
          </Box>
        ) : null}

        {showFooter ? (
          <LensCardFooterRow
            duplicateOf={duplicateOf}
            footer={footer}
            reserveResizeHandle={showActions}
            state={state}
            status={chip}
            tight={tight}
            tokens={tokens}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function LensCardLoadingBody({ kind, reduceMotion }: { kind: LensCardLoadingKind; reduceMotion: boolean }) {
  const animation = reduceMotion ? false : 'wave';
  if (kind === 'metric') {
    return (
      <Stack alignItems="center" spacing={1} sx={{ width: '100%' }}>
        <Skeleton animation={animation} height={40} variant="rounded" width="55%" />
        <Skeleton animation={animation} height={14} variant="text" width="35%" />
      </Stack>
    );
  }
  if (kind === 'table') {
    return (
      <Stack spacing={1} sx={{ width: '100%' }}>
        <Skeleton animation={animation} height={18} variant="text" width="40%" />
        {[100, 92, 96, 85, 90].map((width, index) => (
          <Skeleton animation={animation} height={16} key={index} variant="text" width={`${width}%`} />
        ))}
      </Stack>
    );
  }
  if (kind === 'insight') {
    return (
      <Stack spacing={0.75} sx={{ width: '100%' }}>
        {[100, 96, 88, 60].map((width, index) => (
          <Skeleton animation={animation} height={14} key={index} variant="text" width={`${width}%`} />
        ))}
      </Stack>
    );
  }
  // Chart: a row of bars of uneven height, closer to the eventual plot than a
  // spinner or the literal words "No query connected" ever were.
  return (
    <Stack alignItems="flex-end" direction="row" spacing={1} sx={{ height: '100%', width: '100%' }}>
      {[45, 70, 55, 85, 60, 75, 40].map((height, index) => (
        <Skeleton animation={animation} height={`${height}%`} key={index} sx={{ flex: 1 }} variant="rectangular" />
      ))}
    </Stack>
  );
}

function LensCardEmptyBody({ empty }: { empty: LensCardShellEmpty }) {
  return (
    <Stack alignItems="center" spacing={0.5} sx={{ textAlign: 'center', width: '100%', px: 2 }}>
      <Typography variant="body2">{empty.headline}</Typography>
      {empty.subtitle && (
        <Typography color="text.secondary" variant="caption">
          {empty.subtitle}
        </Typography>
      )}
      {empty.actionLabel && empty.onAction && (
        <Button
          disabled={empty.actionDisabled}
          onClick={(event) => {
            event.stopPropagation();
            empty.onAction?.();
          }}
          size="small"
          variant="outlined"
          sx={{ mt: 0.5 }}
        >
          {empty.actionLabel}
        </Button>
      )}
    </Stack>
  );
}

function LensCardErrorBody({
  error,
  detailsOpen,
  onToggleDetails,
  reduceMotion,
}: {
  error: LensCardShellError;
  detailsOpen: boolean;
  onToggleDetails: () => void;
  reduceMotion: boolean;
}) {
  return (
    <Stack
      alignItems="center"
      spacing={1}
      sx={{ color: 'text.secondary', maxWidth: 520, mx: 'auto', px: 2, py: 1, textAlign: 'center', width: '100%' }}
    >
      <ErrorOutlineIcon color="error" />
      <Typography color="text.primary" fontWeight={600} variant="body2">
        {error.message}
      </Typography>
      {error.guidance && (
        <Typography color="text.secondary" variant="caption">
          {error.guidance}
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        {error.actionLabel && error.onAction && (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              error.onAction?.();
            }}
            size="small"
            variant="contained"
          >
            {error.actionLabel}
          </Button>
        )}
        {error.secondaryActionLabel && error.onSecondaryAction && (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              error.onSecondaryAction?.();
            }}
            size="small"
            variant="outlined"
          >
            {error.secondaryActionLabel}
          </Button>
        )}
        {error.onRetry && (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              error.onRetry?.();
            }}
            size="small"
            startIcon={<RefreshIcon fontSize="small" />}
          >
            Retry
          </Button>
        )}
        {error.detail && (
          <Button aria-expanded={detailsOpen} onClick={onToggleDetails} size="small">
            {detailsOpen ? 'Hide details' : 'Show details'}
          </Button>
        )}
      </Stack>
      {error.detail && (
        <Collapse in={detailsOpen} sx={{ width: '100%' }} timeout={reduceMotion ? 0 : undefined}>
          <Box
            component="pre"
            sx={{
              bgcolor: 'action.hover',
              borderRadius: `${LENS_LAYOUT.cardRadius}px`,
              fontFamily: 'monospace',
              fontSize: 12,
              m: 0,
              maxHeight: 120,
              overflow: 'auto',
              p: 1,
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {error.detail}
          </Box>
        </Collapse>
      )}
    </Stack>
  );
}

function LensCardRestrictedBody({
  title,
  footer,
  restricted,
}: {
  title: string;
  footer: LensCardShellFooter;
  restricted?: LensCardShellEmpty;
}) {
  const [copied, setCopied] = useState(false);
  const headline = restricted?.headline ?? 'You do not have access to the values in this card.';

  // There is no request-access endpoint to call here, so the fallback is
  // honest about what it does: put a description of the block on the
  // clipboard for the viewer to paste to whoever can grant access.
  const copyBlockedDescription = async () => {
    try {
      await navigator.clipboard.writeText([`Card: ${title}`, `Source: ${footer.source}`, headline].join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // A denied clipboard permission is not worth an error state here either.
      setCopied(false);
    }
  };

  return (
    <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary', textAlign: 'center' }}>
      <LockOutlinedIcon />
      <Typography variant="body2">{headline}</Typography>
      {restricted?.subtitle && (
        <Typography color="text.secondary" variant="caption">
          {restricted.subtitle}
        </Typography>
      )}
      {restricted?.actionLabel && restricted.onAction ? (
        <Button onClick={restricted.onAction} size="small" sx={{ mt: 0.5 }}>
          {restricted.actionLabel}
        </Button>
      ) : (
        <Button onClick={copyBlockedDescription} size="small" sx={{ mt: 0.5 }}>
          {copied ? 'Copied' : 'Copy details'}
        </Button>
      )}
    </Stack>
  );
}

function LensCardFooterRow({
  duplicateOf,
  footer,
  reserveResizeHandle = false,
  state,
  status,
  tight = false,
  tokens,
}: {
  duplicateOf?: string;
  footer: LensCardShellFooter;
  /** In edit mode the canvas draws its resize grip over the card's bottom-right
   *  corner, exactly where the contract label sits; leave it room. */
  reserveResizeHandle?: boolean;
  state: LensCardState;
  status: ContractChipInfo;
  tight?: boolean;
  tokens: ReturnType<typeof lensTokens>;
}) {
  const theme = useTheme();
  // Dataset provenance lives in the card menu with the other inspection
  // actions. The face only keeps transient execution detail for the rare
  // footer states that still need a row.
  const provenance = [footer.freshness, state !== 'truncated' ? footer.rowCountText : undefined]
    .filter(Boolean)
    .join(' · ');
  const nodes: Array<React.ReactNode> = [];
  if (footer.detail) {
    nodes.push(
      <Typography color="text.secondary" key="detail" sx={{ fontSize: 11 }} variant="caption">
        {footer.detail}
      </Typography>,
    );
  }
  if (footer.restrictedNote) {
    nodes.push(
      <Typography
        key="restricted"
        sx={{ color: theme.palette.warning.main, fontSize: 11, fontWeight: 600 }}
        variant="caption"
      >
        {footer.restrictedNote}
      </Typography>,
    );
  }
  if (duplicateOf) {
    nodes.push(
      <Tooltip key="duplicate" title={`This card repeats the same result as “${duplicateOf}”.`}>
        <Chip
          aria-label={`Duplicate of ${duplicateOf}`}
          label="Duplicate"
          size="small"
          tabIndex={0}
          sx={{
            // Keep a diagnostic in the provenance row, never painted over the
            // title or KPI. Ocean Teal is the structural colour in light mode;
            // the mode-adaptive accent keeps the chip legible on a dark card.
            backgroundColor: alpha(tokens.accent, 0.12),
            border: `1px solid ${alpha(tokens.accent, 0.32)}`,
            color: tokens.accent,
            fontFamily: FONT_STACK_SUISSE_INTL,
            fontSize: 11,
            fontWeight: 600,
            height: 18,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      </Tooltip>,
    );
  }

  // Every card carries a governance signal, but the unmarked default (most
  // cards today) shows nothing on the row itself: sixteen "Uncertified" words
  // on a board is noise a marked state would no longer stand out against. The
  // fact is not dropped, only moved to where a reader who wants it can find
  // it: the row's hover tooltip and its accessible name.
  const isMarked = !status.quiet;
  const footerTitle = status.quiet
    ? [provenance, 'Not certified'].filter(Boolean).join(' · ')
    : provenance || undefined;

  return (
    <Box
      aria-label={status.quiet ? 'Not certified' : undefined}
      sx={{
        flexShrink: 0,
        // A hairline: text.primary at low alpha reads the same on Compass White and on the dark
        // teal card, where the theme divider itself is too faint to see.
        borderTop: `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.08)}`,
        mt: tight ? 0.5 : 1,
        pr: reserveResizeHandle ? 4 : 0,
        pt: tight ? 0.5 : 1,
      }}
      title={footerTitle}
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
        <Stack alignItems="center" direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', minWidth: 0, rowGap: 0.5 }}>
          {withDotSeparators(nodes)}
        </Stack>
        {/* The unmarked default renders nothing here on purpose: an absent
            label used to read as certified by omission, which the tooltip and
            aria-label above now state plainly instead. A marked state keeps
            its own dot and coloured label so it still stands out against the
            quiet common case. */}
        {isMarked && (
          <Tooltip title={status.basis}>
            <Stack
              alignItems="center"
              aria-label={`${status.label}. ${status.basis}`}
              component="span"
              direction="row"
              spacing={0.5}
              sx={{ cursor: 'default', flexShrink: 0 }}
              tabIndex={0}
            >
              <Box
                sx={{
                  bgcolor: status.color,
                  borderRadius: '50%',
                  flexShrink: 0,
                  height: 6,
                  width: 6,
                }}
              />
              <Typography
                component="span"
                sx={{
                  color: status.color,
                  fontFamily: FONT_STACK_SUISSE_INTL,
                  fontSize: 11,
                  fontWeight: 500,
                }}
                variant="caption"
              >
                {status.label}
              </Typography>
            </Stack>
          </Tooltip>
        )}
      </Stack>
      {state === 'truncated' && footer.rowCountText && (
        <Box
          sx={{
            bgcolor: tokens.truncatedBg,
            border: `1px solid ${tokens.truncatedBorder}`,
            borderRadius: `${LENS_LAYOUT.cardRadius}px`,
            mt: 0.5,
            px: 1,
            py: 0.5,
          }}
        >
          <Typography
            sx={{
              color: tokens.truncatedBorder,
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 600,
            }}
            variant="caption"
          >
            {footer.rowCountText}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/** Inserts a middle-dot separator only between segments that actually rendered. */
function withDotSeparators(nodes: Array<React.ReactNode>): Array<React.ReactNode> {
  return nodes.flatMap((node, index) =>
    index === 0
      ? [node]
      : [
          <Typography color="text.secondary" key={`dot-${index}`} sx={{ fontSize: 11 }} variant="caption">
            ·
          </Typography>,
          node,
        ],
  );
}
